import * as Dialog from "@radix-ui/react-dialog";
import { Cross2Icon } from "@radix-ui/react-icons";
import { Icon } from "../Icon/Icon";
import { useTheme } from "../Theme/ThemeProvider";
import React, { useRef, useState, useEffect } from "react";
import { ThemeFontSlider } from "./ThemeFontSlider";
import { getUserInitials } from "../../utils/userUtils";
import { useAccount, useMsal } from "@azure/msal-react";
import { USER_BUBBLE_COLORS, setCookie, getSavedBubbleColor } from '../../utils/bubbleColors';
import { createUserPreferencesService } from '../../utils/userPreferences.service';
import { useAppSelector } from '../../store/hooks';
import logger from '../../utils/logger';

function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(val, max));
}

function removeCookieValue(name: string) {
  if (typeof document === 'undefined') return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Strict`;
}

interface ThemeDialogProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

/** Contains accessibility settings such as font size, accent color, dark vs. light mode */
export function ThemeDialog({ open, onOpenChange }: ThemeDialogProps = {}) {
  const { mode, setMode, accent, fontSize, isUsingSystemTheme, fontSizeLabels } = useTheme();
  const account = useAccount();
  const { instance: msalInstance } = useMsal();
  const savedPrefs = useAppSelector((state) => state.userPreferences);
  // Always keep a ref in sync with the latest savedPrefs so that effects never
  // read a stale snapshot (effects only re-run when their listed deps change,
  // so savedPrefs in a closure can be outdated if it's not in the dep array).
  const savedPrefsRef = useRef(savedPrefs);
  savedPrefsRef.current = savedPrefs;
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const dragging = useRef(false);
  const offset = useRef({ x: 0, y: 0 });
  const boxRef = useRef<HTMLDivElement>(null);
  const [boxSize, setBoxSize] = useState({ width: 0, height: 0 });
  
  // User bubble color state with cookie fallback
  const [userBubbleColor, setUserBubbleColor] = useState(() => getSavedBubbleColor());

  // Save user bubble color to both localStorage and cookie when it changes
  useEffect(() => {
    localStorage.setItem('user-bubble-color', userBubbleColor);
    setCookie('user-bubble-color', userBubbleColor);
    // Notify other components (e.g. MobileMenu) that the bubble color changed
    window.dispatchEvent(new Event('user-bubble-color-change'));
  }, [userBubbleColor]);

  // Fire PUT to backend when the user actively changes bubble color.
  // This is called directly from the click handler (not an effect) to ensure it
  // only fires on genuine user interaction, never on mount or programmatic updates.
  const handleColorSelect = (colorValue: string) => {
    setUserBubbleColor(colorValue);
    const colorName = USER_BUBBLE_COLORS.find(c => c.value === colorValue)?.name ?? colorValue;
    if (savedPrefsRef.current.hasFetched && savedPrefsRef.current.bubbleColor === colorName) return;
    createUserPreferencesService(msalInstance)
      .updateUserPreferences({ bubbleColor: colorName })
      .then(() => logger.log('[UserPreferences] bubbleColor saved:', colorName))
      .catch(err => console.error('[UserPreferences] Failed to save bubbleColor:', err));
  };

  // Fire PUT to backend when the user actively changes font size.
  // Called directly from ThemeFontSlider's onUserChange callback.
  const handleFontSizeChange = (value: 0 | 1 | 2) => {
    const label = fontSizeLabels[value];
    if (savedPrefsRef.current.hasFetched && savedPrefsRef.current.fontSize === label) return;
    createUserPreferencesService(msalInstance)
      .updateUserPreferences({ fontSize: label })
      .then(() => logger.log('[UserPreferences] fontSize saved:', label))
      .catch(err => console.error('[UserPreferences] Failed to save fontSize:', err));
  };

  useEffect(() => {
    if (boxRef.current) {
      const rect = boxRef.current.getBoundingClientRect();
      setBoxSize({ width: rect.width, height: rect.height });
    }
  }, [mode, accent]);

  /** Mouse event handlers for dragging the dialog */
  function onMouseDown(e: React.MouseEvent) {
    dragging.current = true;
    offset.current = {
      x: e.clientX - position.x,
      y: e.clientY - position.y,
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  }

  function onTouchStart(e: React.TouchEvent) {
    if (e.touches.length > 0) {
      dragging.current = true;
      offset.current = {
        x: e.touches[0].clientX - position.x,
        y: e.touches[0].clientY - position.y,
      };
      document.addEventListener("touchmove", onTouchMove);
      document.addEventListener("touchend", onTouchEnd);
    }
  }

  function onMouseMove(e: MouseEvent) {
    if (dragging.current && boxRef.current) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let newX = e.clientX - offset.current.x;
      let newY = e.clientY - offset.current.y;
      const minX = -(vw / 2) + boxSize.width / 2;
      const maxX = vw / 2 - boxSize.width / 2;
      const minY = -(vh / 2) + boxSize.height / 2;
      const maxY = vh / 2 - boxSize.height / 2;
      setPosition({
        x: clamp(newX, minX, maxX),
        y: clamp(newY, minY, maxY),
      });
    }
  }

  function onTouchMove(e: TouchEvent) {
    if (dragging.current && boxRef.current && e.touches.length > 0) {
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      let newX = e.touches[0].clientX - offset.current.x;
      let newY = e.touches[0].clientY - offset.current.y;
      const minX = -(vw / 2) + boxSize.width / 2;
      const maxX = vw / 2 - boxSize.width / 2;
      const minY = -(vh / 2) + boxSize.height / 2;
      const maxY = vh / 2 - boxSize.height / 2;
      setPosition({
        x: clamp(newX, minX, maxX),
        y: clamp(newY, minY, maxY),
      });
    }
  }

  function onMouseUp() {
    dragging.current = false;
    document.removeEventListener("mousemove", onMouseMove);
    document.removeEventListener("mouseup", onMouseUp);
  }

  function onTouchEnd() {
    dragging.current = false;
    document.removeEventListener("touchend", onTouchEnd);
    document.removeEventListener("touchmove", onTouchMove);
  }

  // Mode and accent are now provided directly by the theme context
  // No need to parse or create custom setters
  const showResetButton = !isUsingSystemTheme || fontSize !== 1 || userBubbleColor !== 'default';
  
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      {open === undefined && (
        <Dialog.Trigger asChild>
          <button className="absolute inset-0 w-full h-full opacity-0 cursor-pointer" aria-label="Open theme settings" />
        </Dialog.Trigger>
      )}
      <Dialog.Portal>
        <Dialog.Content asChild>
          {/* Outer shell: flex column, capped height. X button is a non-scrolling row at top. */}
          <div
            ref={boxRef}
            className="bg-[var(--color-secondary)] rounded-lg"
            style={{
              position: "fixed",
              left: `calc(50% + ${position.x}px)`,
              top: `calc(50% + ${position.y}px)`,
              transform: "translate(-50%, -50%)",
              zIndex: 30000,
              boxShadow: `0 6px 18px 6px var(--color-secondary), 2px 4px 16px 4px rgba(0, 0, 0, 0.5)`,
              maxWidth: 'min(90vw, 600px)',
              maxHeight: '90vh',
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {/* X close button — absolute on the outer shell, out of flow, so it never covers scrolling content */}
            <Dialog.Close
              className="p-1 rounded-full transition-colors"
              style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 10, color: 'var(--color-text)', background: 'transparent' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(128,128,128,0.2)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
              aria-label="Close"
            >
              <Cross2Icon width={18} height={18} />
            </Dialog.Close>
            {/* Inner scrollable content — paddingTop leaves room so the X button doesn't overlap the title */}
            <div
              className="text-text p-6 flex flex-col items-center"
              style={{
                overflowY: 'auto',
                flex: '1 1 auto',
                paddingTop: '36px',
              }}
            >
            {/* Only the title is draggable, in future could make side, etc. draggable too */}
            <Dialog.Title
              className="mb-4 text-lg font-semibold select-none cursor-move w-full text-center"
              onMouseDown={onMouseDown}
              onTouchStartCapture={onTouchStart}
            >
              Customize Theme
              {isUsingSystemTheme && (
                <div className="text-xs font-normal opacity-70 mt-1">
                  Following system theme
                </div>
              )}
            </Dialog.Title>
            {/* Light/Dark mode toggle */}
            <div className="flex gap-2 items-center">
              <button
                className="relative px-6 py-4 flex flex-col items-center justify-center rounded-lg transition-all border-2"
                onClick={() => setMode("light")}
                aria-label="Light mode"
                style={{
                  minWidth: '100px',
                  borderColor: 'var(--color-text)',
                  opacity: mode === "light" ? 1 : 0.5,
                  boxShadow: mode === "light" ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' : 'none',
                }}
                onMouseEnter={(e) => {
                  if (mode !== "light") {
                    e.currentTarget.style.opacity = '0.7';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = mode === "light" ? '1' : '0.5';
                }}
              >
                {mode === "light" && (
                  <div className="absolute top-1 right-1">
                    <Icon name="check" width={16} height={16} strokeWidth={3} />
                  </div>
                )}
                <Icon name="sun" width={28} height={28} strokeWidth={10} />
                <span className="text-xs mt-2 font-medium">Light</span>
              </button>
              <button
                className="relative px-6 py-4 flex flex-col items-center justify-center rounded-lg transition-all border-2"
                onClick={() => setMode("dark")}
                aria-label="Dark mode"
                style={{
                  minWidth: '100px',
                  borderColor: 'var(--color-text)',
                  opacity: mode === "dark" ? 1 : 0.5,
                  boxShadow: mode === "dark" ? '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)' : 'none',
                }}
                onMouseEnter={(e) => {
                  if (mode !== "dark") {
                    e.currentTarget.style.opacity = '0.7';
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.opacity = mode === "dark" ? '1' : '0.5';
                }}
              >
                {mode === "dark" && (
                  <div className="absolute top-1 right-1">
                    <Icon name="check" width={16} height={16} strokeWidth={3} />
                  </div>
                )}
                <Icon name="moon" width={28} height={28} />
                <span className="text-xs mt-2 font-medium">Dark</span>
              </button>
            </div>
            
            {/* User Bubble Color Section */}
            <div className="w-full mt-12 mb-12">
              <h3 className="text-sm font-semibold mb-3 text-center text-[var(--color-text)]">
                Your Chat Bubble Color
              </h3>
              
              {/* Preview bubble with user initials */}
              <div className="flex justify-center mb-4">
                {(() => {
                  // If default, use mode-specific colors
                  if (userBubbleColor === 'default') {
                    return (
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center font-semibold shadow-lg border-2"
                        style={{
                          background: mode === 'dark' ? '#313136' : '#e5e7eb',
                          borderColor: 'var(--color-border)',
                          color: mode === 'dark' ? '#ffffff' : '#1f2937'
                        }}
                      >
                        {getUserInitials(account?.name)}
                      </div>
                    );
                  }
                  
                  const meta = USER_BUBBLE_COLORS.find(c => c.value === userBubbleColor);
                  const tone = meta?.textTone || 'light';
                  // Force a dark readable font for explicitly light backgrounds (Aurora, Lavender, Peachy, Gray) regardless of overall theme.
                  const forceDark = tone === 'dark' || (tone === 'auto' && mode === 'light');
                  return (
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center font-semibold shadow-lg border-2"
                      style={{
                        background: userBubbleColor,
                        borderColor: 'var(--color-border)',
                        color: forceDark ? '#1f2937' : '#ffffff'
                      }}
                    >
                      {getUserInitials(account?.name)}
                    </div>
                  );
                })()}
              </div>
              
              {/* Color options for chat bubble */}
              <div className="grid grid-cols-6 gap-2 max-w-xs mx-auto">
                {USER_BUBBLE_COLORS.map((color, index) => {
                  const tone = color.textTone || 'light';
                  const forceDark = tone === 'dark' || (tone === 'auto' && mode === 'light');
                  return (
                    <button
                      key={index}
                      className={`w-8 h-8 rounded-full border-2 transition-all duration-200 hover:scale-110 flex items-center justify-center ${
                        userBubbleColor === color.value
                          ? 'border-white shadow-lg scale-110'
                          : 'border-gray-300 hover:border-white'
                      }`}
                      style={{
                        background: color.value === 'default'
                          ? (mode === 'dark' ? '#313136' : '#e5e7eb')
                          : color.value,
                        color: color.value === 'default' 
                          ? (mode === 'dark' ? '#ffffff' : '#1f2937')
                          : (forceDark ? '#1f2937' : '#ffffff')
                      }}
                      onClick={() => handleColorSelect(color.value)}
                      title={color.name}
                      aria-label={`Select ${color.name} color`}
                    >
                      {color.value === 'default' && (
                        <span className="text-xs font-bold" style={{paddingTop: '0.5px'}}>D</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Accent color choices, commented out for now */}
            {/* <ThemeAccentColors accent={accent} setAccent={setAccent} accentColors={ACCENT_LIST} /> */}
            {/* Font size choices. TODO: when window is small change to S M L instead of Small Medium Large */}
            <ThemeFontSlider onUserChange={handleFontSizeChange} />
            
            <div className="flex gap-3 mt-6">
              {showResetButton && (
                <button
                  className="px-3 py-1 rounded border border-[var(--color-accent-hover)] bg-[var(--button-background-color)] text-[var(--color-text)] transition text-xs"
                  style={{
                    backgroundColor: 'var(--button-background-color)',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'rgb(49, 49, 54)';
                    // Keep text color as is in dark mode, change to bg color in light mode
                    if (mode === 'light') {
                      e.currentTarget.style.color = 'var(--color-bg)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--button-background-color)';
                    e.currentTarget.style.color = 'var(--color-text)';
                  }}
                  onClick={() => {
                    localStorage.removeItem('theme-mode');
                    localStorage.removeItem('theme-accent');
                    localStorage.removeItem('theme-user-preference');
                    localStorage.removeItem('theme-font-size');
                    localStorage.removeItem('user-bubble-color');
                    localStorage.removeItem('timeFormatPref');
                    removeCookieValue('theme-mode');
                    removeCookieValue('theme-accent');
                    removeCookieValue('theme-user-preference');
                    removeCookieValue('theme-font-size');
                    removeCookieValue('user-bubble-color');
                    removeCookieValue('timeFormatPref');
                    setTimeout(() => {
                      window.location.replace(window.location.href);
                    }, 100);
                  }}
                >
                  Reset to System Defaults
                </button>
              )}
            </div>
            </div>{/* end inner scrollable */}
          </div>{/* end outer shell */}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}